// src/config/app.constants.js
// ─────────────────────────────────────────────────────────────────────────────
//  App-wide magic value constants.
//
//  PHASE 5: All timing, size, and quota values previously scattered as inline
//  literals across syncManager.js, useSyncStore.js, firestore.js, and
//  SyncStatusContext.jsx are now defined here as named exports.
//
//  RULE: Never use a bare number for any of these concerns. Import the constant
//  and use the name — changing behaviour app-wide is then a one-line edit here.
//
//  Usage:
//    import { SYNC_DEBOUNCE_MS, FIRESTORE_QUERY_LIMIT } from '@/config/app.constants';
// ─────────────────────────────────────────────────────────────────────────────

// ── Sync / write debounce ─────────────────────────────────────────────────────
/**
 * How long (ms) to wait after the last setValue() call before pushing to
 * Firestore. IDB is written immediately; only the Firestore push is delayed.
 * 1.2 s gives a natural pause between keystrokes without feeling laggy.
 *
 * Used in: useSyncStore.js
 */
export const SYNC_DEBOUNCE_MS = 1_200;

/**
 * Suppression window (ms) after a local write during which incoming Firestore
 * snapshots for the same key are ignored. Prevents the remote echo of our own
 * write from overwriting the optimistic local state.
 *
 * Used in: useSyncStore.js
 */
export const WRITE_SUPPRESS_MS = 500;

// ── Firestore fallback timeout ────────────────────────────────────────────────
/**
 * Maximum time (ms) useSyncStore waits for a Firestore fallback read before
 * giving up and rendering with the local IDB value (or initialValue).
 *
 * Raised from 2 000 → 8 000 ms: waitForAuth() inside firestoreGet() consumes
 * part of the window before the actual getDoc() even starts. On slow or
 * cold-start connections this left too little time for the read itself,
 * causing spurious "Firestore fallback failed" warnings for keys like
 * themeMode. This path only fires when IDB is empty (new device / first
 * login); returning users hit it zero times. setReady(true) is always called
 * in the finally block so no render is blocked longer than this value.
 *
 * Used in: useSyncStore.js
 */
export const FIRESTORE_FALLBACK_TIMEOUT_MS = 8_000;

// ── Sync flush intervals ───────────────────────────────────────────────────────
/**
 * How often (ms) syncManager retries the pending queue in the background
 * while the tab is open and the device is online.
 *
 * Used in: syncManager.js
 */
export const PERIODIC_FLUSH_INTERVAL_MS = 5 * 60 * 1_000; // 5 minutes

/**
 * Delay (ms) before the first retry after a failed online write.
 * Short enough to feel near-instant on reconnect; long enough not to thrash.
 *
 * Used in: syncManager.js (scheduledFlush)
 */
export const SCHEDULED_FLUSH_DELAY_MS = 2_000;

// ── Quota backoff ──────────────────────────────────────────────────────────────
/**
 * How long (ms) to pause all Firestore writes after a resource-exhausted error.
 * Firestore free-tier quota resets at midnight Pacific — 1 hour is a safe
 * middle ground that doesn't burn further quota on retries.
 *
 * Used in: syncManager.js
 */
export const QUOTA_BACKOFF_MS = 60 * 60 * 1_000; // 1 hour

// ── Firestore query safety limits ─────────────────────────────────────────────
/**
 * Default limit() value for any Firestore collection query.
 * The free Spark plan allows 50k reads/day — unbounded queries on large
 * collections burn this quota instantly. Always pass this to .limit().
 *
 * NOTE: The current app uses point-document reads (users/{uid}/data/{key})
 * so limit() is not yet applied anywhere. This constant is defined now so
 * that any future collection query is forced to use an explicit limit rather
 * than leaving it as an undocumented magic number.
 *
 * Used in: any future firestore.js collection query
 */
export const FIRESTORE_QUERY_LIMIT = 100;

/**
 * Maximum number of operations in a single Firestore writeBatch() call.
 * Firestore hard limit is 500 — we stay well below to leave headroom.
 *
 * Used in: syncManager.js (flushPendingQueue)
 */
export const FIRESTORE_BATCH_SIZE = 400;

// ── Sync status polling ────────────────────────────────────────────────────────
/**
 * How often (ms) SyncStatusContext polls the IDB pendingQueue to update the
 * pending count displayed in OfflineBanner and SyncStatusBadge.
 * 8 s is frequent enough to feel live without burning CPU on low-end devices.
 *
 * Used in: SyncStatusContext.jsx
 */
export const SYNC_STATUS_POLL_INTERVAL_MS = 8_000;

// ── Verse clipboard ────────────────────────────────────────────────────────────
/**
 * Maximum number of highlighted verses stored in the verse clipboard (FIFO).
 * Keeping this small prevents the IDB value from growing unbounded — the
 * clipboard is a convenience feature, not a permanent archive.
 *
 * Used in: Bible.jsx (verse clipboard logic)
 */
export const VERSE_CLIPBOARD_MAX = 20;

// ── IDB connection health ──────────────────────────────────────────────────────
/**
 * Maximum number of automatic reconnect retries for a failed IDB operation
 * before the error is re-thrown to the caller.
 * Currently: 1 retry (open a fresh connection and try once more).
 *
 * Used in: indexedDB.js (withRetry)
 */
export const IDB_MAX_RETRIES = 1;

// ── App dates ──────────────────────────────────────────────────────────────────
/**
 * The start date of the church-plant ministry year used to calculate ministry
 * month offsets throughout the app (Layout stewardship summary, DiscipleshipKeeper,
 * Stewardship, useProfileContent).
 *
 * BUNDLE NOTE: This constant is intentionally duplicated here from
 * dailyDevotionals.js. Layout.jsx is NOT lazy-loaded (it wraps every route),
 * so any import from dailyDevotionals.js pulls the entire 826 kB module into
 * the main bundle on every page load. By importing this single Date value from
 * app.constants instead, Layout.jsx no longer depends on dailyDevotionals.js
 * at all — the large module is only loaded when a lazy page that needs it
 * (Stewardship, DiscipleshipKeeper) is actually visited.
 *
 * dailyDevotionals.js still exports its own copy — do not remove that export,
 * as the lazy pages import from there directly.
 *
 * Used in: Layout.jsx (stewardship summary ministry-month calculation)
 */
export const CHURCH_PLANT_START_DATE = new Date('2026-03-01');

// ── Public site ────────────────────────────────────────────────────────────────
/**
 * Production origin used to build shareable links (e.g. the public prayer
 * request form at /pray/<token>). Hardcoded because inside the Capacitor app
 * window.location.origin is not the public web address.
 *
 * Used in: PrayerLinkDialog.jsx
 */
export const PUBLIC_SITE_URL = 'https://disciplesheep.space';